import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const REASON_LABEL = {
    after_hours: 'After-hours emergency',
    walk_in: 'Walk-in / in-person',
    verbal_auth: 'Verbal authorisation',
    engineer_forgot: 'Engineer forgot to log',
    system_outage: 'System was down',
    migration: 'Legacy / migration import',
    other: 'Other',
};
function formatGap(occurredAt, createdAt) {
    const occ = new Date(occurredAt).getTime();
    const cre = new Date(createdAt).getTime();
    const secs = Math.max(0, Math.floor((cre - occ) / 1000));
    const days = Math.floor(secs / 86_400);
    const hours = Math.floor((secs % 86_400) / 3_600);
    if (days >= 1) {
        return hours ? `${days}d ${hours}h` : `${days}d`;
    }
    if (hours >= 1)
        return `${hours}h`;
    const mins = Math.floor((secs % 3_600) / 60);
    return mins ? `${mins}m` : '<1m';
}
function formatLocal(iso, opts) {
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat(undefined, {
            day: '2-digit',
            month: 'short',
            year: opts?.withYear ? 'numeric' : undefined,
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
        }).format(d);
    }
    catch {
        return iso;
    }
}
function formatSite(iso, siteTimezone, opts) {
    if (!siteTimezone)
        return formatLocal(iso, opts);
    try {
        const d = new Date(iso);
        return new Intl.DateTimeFormat(undefined, {
            day: '2-digit',
            month: 'short',
            year: opts?.withYear ? 'numeric' : undefined,
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short',
            timeZone: siteTimezone,
        }).format(d);
    }
    catch {
        return iso;
    }
}
export function BackdatedBanner({ ticket }) {
    if (!ticket.occurred_at)
        return null;
    const reason = ticket.backdate_reason_code
        ? REASON_LABEL[ticket.backdate_reason_code] ?? ticket.backdate_reason_code
        : '—';
    const gap = formatGap(ticket.occurred_at, ticket.created_at);
    return (_jsxs("div", { className: "rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/30", children: [_jsx("div", { className: "mb-1 flex items-center gap-1.5 text-sm font-semibold text-amber-900 dark:text-amber-200", children: "\uD83D\uDCC5 Backdated ticket" }), _jsxs("dl", { className: "grid grid-cols-1 gap-x-4 gap-y-0.5 text-amber-900 sm:grid-cols-[max-content_1fr] dark:text-amber-200", children: [_jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Logged in system" }), _jsx("dd", { children: formatLocal(ticket.created_at, { withYear: true }) }), _jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Actually happened" }), _jsx("dd", { children: formatSite(ticket.occurred_at, ticket.site_timezone, { withYear: true }) }), _jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Backdated by" }), _jsx("dd", { children: gap }), _jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Reason" }), _jsx("dd", { children: reason }), ticket.backdate_reason_note && (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Justification" }), _jsxs("dd", { className: "whitespace-pre-wrap", children: ["\u201C", ticket.backdate_reason_note, "\u201D"] })] })), ticket.backdated_by_display_name && (_jsxs(_Fragment, { children: [_jsx("dt", { className: "text-amber-700 dark:text-amber-300", children: "Logged by" }), _jsx("dd", { children: ticket.backdated_by_display_name })] }))] })] }));
}
export function BackdatedBadge({ occurredAt, createdAt, reasonCode }) {
    if (!occurredAt)
        return null;
    const gap = formatGap(occurredAt, createdAt);
    const reasonLabel = reasonCode ? REASON_LABEL[reasonCode] ?? reasonCode : 'backdated';
    return (_jsxs("span", { title: `Backdated by ${gap} — reason: ${reasonLabel}`, className: "inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300", children: ["\uD83D\uDCC5 Backdated ", gap] }));
}
